import React from 'react';
import { Users, DollarSign, AlertTriangle, CalendarClock, Activity } from 'lucide-react';
import StatCard from '../ui/StatCard';
import { summarizeUsers } from './utilizationHelpers';

/**
 * Row of 5 top-level stat cards shown above the Insights view charts.
 *
 * Accepts an optional `onCardClick(cardId)` callback. When provided, each
 * card becomes interactive and dispatches the id of the clicked card so the
 * parent (InsightsView) can open the drill-in drawer with the right subset
 * of employees. Ids map to:
 *   - 'total'         → everyone matching the filters
 *   - 'billable'      → employees with billable hours in the selected range
 *   - 'overallocated' → employees with is_over_allocated = true
 *   - 'free'          → employees free before month end
 *   - 'avg'           → everyone, sorted by utilization
 */
const SummaryStats = ({ users = [], onCardClick }) => {
  const s = summarizeUsers(users);
  const handle = (id) =>
    typeof onCardClick === 'function' ? () => onCardClick(id) : undefined;

  return (
    <div
      data-testid="resource-summary-stats"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
    >
      <StatCard
        icon={Users}
        label="Employees"
        value={s.totalEngineers}
        tone="primary"
        onClick={handle('total')}
      />
      <StatCard
        icon={DollarSign}
        label="With billable work"
        value={s.withBillable}
        tone="green"
        onClick={handle('billable')}
      />
      <StatCard
        icon={AlertTriangle}
        label="Over-allocated"
        value={s.overAllocated}
        tone={s.overAllocated > 0 ? 'red' : 'amber'}
        onClick={handle('overallocated')}
      />
      <StatCard
        icon={CalendarClock}
        label="Free this month"
        value={s.freeThisMonth}
        tone="purple"
        onClick={handle('free')}
      />
      <StatCard
        icon={Activity}
        label="Avg utilization"
        value={`${s.avgUtilization}%`}
        tone="blue"
        onClick={handle('avg')}
      />
    </div>
  );
};

export default SummaryStats;
